import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import '../styles/Announcement.css';
import {
  FiEdit2, FiTrash2, FiUpload, FiCalendar,
  FiChevronLeft, FiChevronRight, FiX,
  FiFileText, FiImage, FiCheck, FiGrid, FiSliders,
  FiSearch, FiDownload, FiFilter, FiEye,
  FiChevronsLeft, FiChevronsRight, FiBell, FiToggleLeft, FiToggleRight
} from 'react-icons/fi';
import { UPLOAD_URL } from '../apolloClient';

// GraphQL Queries and Mutations
const GET_ALL_ANNOUNCEMENTS = gql`
  query {
    getAllAnnouncements {
      id
      imageUrl
      imagePath
      title
      message
      isActive
      createdAt
      updatedAt
    }
  }
`;

const CREATE_ANNOUNCEMENT = gql`
  mutation CreateAnnouncement(
    $title: String!
    $message: String!
    $isActive: Boolean!
    $imageUrl: String!
    $imagePath: String!
  ) {
    createAnnouncement(
      title: $title
      message: $message
      isActive: $isActive
      imageUrl: $imageUrl
      imagePath: $imagePath
    ) {
      success
      message
    }
  }
`;

const UPDATE_ANNOUNCEMENT = gql`
  mutation UpdateAnnouncement(
    $id: ID!
    $title: String
    $message: String
    $isActive: Boolean
    $imageUrl: String
    $imagePath: String
  ) {
    updateAnnouncement(
      id: $id
      title: $title
      message: $message
      isActive: $isActive
      imageUrl: $imageUrl
      imagePath: $imagePath
    ) {
      success
      message
    }
  }
`;

const DELETE_ANNOUNCEMENT = gql`
  mutation DeleteAnnouncement($id: ID!) {
    deleteAnnouncement(id: $id) {
      success
      message
    }
  }
`;

// Utility function to upload image to Firebase
const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", "announcements");

  // Use centralized UPLOAD_URL from apolloClient
  const res = await fetch(UPLOAD_URL, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Upload failed");
  }

  const data = await res.json();

  if (!data.success || !data.url) {
    throw new Error("Invalid upload response");
  }

  return { url: data.url, path: data.path };
};

function AnnouncementPage() {
  // Form states
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const [imagePath, setImagePath] = useState("");

  // UI states
  const [editMode, setEditMode] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [toast, setToast] = useState(null);

  // Filter, search and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  const { loading, error: queryError, data, refetch } = useQuery(GET_ALL_ANNOUNCEMENTS, {
    fetchPolicy: 'network-only',
  });

  const [createAnnouncement] = useMutation(CREATE_ANNOUNCEMENT);
  const [updateAnnouncement] = useMutation(UPDATE_ANNOUNCEMENT);
  const [deleteAnnouncement] = useMutation(DELETE_ANNOUNCEMENT);

  const announcements = useMemo(() => data?.getAllAnnouncements || [], [data]);

  // Filter announcements based on search and filters
  const filteredAnnouncements = useMemo(() => {
    return announcements.filter(announcement => {
      // Text search
      const matchesSearch = !searchTerm ||
        announcement.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.message?.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      let matchesStatus = true;
      if (statusFilter !== "all") {
        switch (statusFilter) {
          case "active":
            matchesStatus = announcement.isActive === true;
            break;
          case "inactive":
            matchesStatus = announcement.isActive === false;
            break;
          default:
            matchesStatus = true;
        }
      }

      return matchesSearch && matchesStatus;
    });
  }, [announcements, searchTerm, statusFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAnnouncements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredAnnouncements.slice(startIndex, endIndex);

  // Toast notification
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Image Upload Handler
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setToast("❌ Only images are allowed");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setToast("❌ File too large (max 20MB)");
      return;
    }

    setIsUploading(true);
    setToast("⏳ Uploading image...");

    try {
      const { url, path } = await uploadImage(file);
      setImageUrl(url);
      setImagePath(path);
      setToast("✅ Image uploaded successfully!");
    } catch (err) {
      console.error(err);
      setToast("❌ Upload error");
    } finally {
      setIsUploading(false);
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setToast("❌ Title is required!");
      return;
    }

    if (!message.trim()) {
      setToast("❌ Message is required!");
      return;
    }

    // Ensure image is uploaded for new announcements
    if (!editMode && (!imageUrl || !imagePath)) {
      setToast("❌ Image is required for new announcements!");
      return;
    }

    try {
      if (editMode) {
        const { data } = await updateAnnouncement({
          variables: {
            id: editMode,
            title,
            message,
            isActive,
            imageUrl: imageUrl || undefined,
            imagePath: imagePath || undefined
          }
        });

        if (data?.updateAnnouncement?.success) {
          setToast("✅ Announcement updated successfully!");
        } else {
          setToast("❌ " + (data?.updateAnnouncement?.message || "Update failed"));
        }
      } else {
        const { data } = await createAnnouncement({
          variables: {
            title,
            message,
            isActive,
            imageUrl,
            imagePath
          }
        });

        if (data?.createAnnouncement?.success) {
          setToast("✅ Announcement created successfully!");
        } else {
          setToast("❌ " + (data?.createAnnouncement?.message || "Creation failed"));
        }
      }

      resetForm();
      refetch();
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setToast("❌ Error: " + err.message);
    }
  };

  const resetForm = () => {
    setTitle("");
    setMessage("");
    setIsActive(true);
    setImageUrl("");
    setImagePath("");
    setEditMode(null);
  };

  const handleEdit = (announcement) => {
    setEditMode(announcement.id);
    setTitle(announcement.title);
    setMessage(announcement.message);
    setIsActive(announcement.isActive);
    setImageUrl(announcement.imageUrl || "");
    setImagePath(announcement.imagePath || "");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      const { data } = await deleteAnnouncement({ variables: { id } });

      if (data?.deleteAnnouncement?.success) {
        setToast("✅ Announcement deleted successfully!");
        refetch();
        if (currentItems.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
        if (expandedCard === id) {
          setExpandedCard(null);
        }
      } else {
        setToast("❌ " + (data?.deleteAnnouncement?.message || "Delete failed"));
      }
    } catch (err) {
      setToast("❌ Delete failed: " + err.message);
    }
  };

  // Toggle card expansion
  const toggleExpandCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Toggle active status
  const toggleActiveStatus = async (id, currentStatus) => {
    try {
      const { data } = await updateAnnouncement({
        variables: {
          id,
          isActive: !currentStatus
        }
      });

      if (data?.updateAnnouncement?.success) {
        setToast(`✅ Announcement ${!currentStatus ? 'activated' : 'deactivated'}!`);
        refetch();
      } else {
        setToast("❌ Failed to update status");
      }
    } catch (err) {
      setToast("❌ Error: " + err.message);
    }
  };

  // Slide Navigation
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % filteredAnnouncements.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + filteredAnnouncements.length) % filteredAnnouncements.length);
  const goToSlide = (index) => setCurrentSlide(index);

  // Pagination handlers
  const goToPage = (page) => setCurrentPage(page);
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  // Export to CSV function - Google Sheets compatible
  const exportToCSV = () => {
    if (filteredAnnouncements.length === 0) {
      setToast("❌ No data to export!");
      return;
    }

    // UTF-8 BOM for Excel/Google Sheets compatibility
    const BOM = "\uFEFF";

    const headers = [
      "ID",
      "Title",
      "Message",
      "Status",
      "Image URL"
    ];

    const rows = filteredAnnouncements.map(a => [
      a.id,
      `"${(a.title || "").replace(/"/g, '""')}"`,
      `"${(a.message || "").replace(/"/g, '""')}"`,
      a.isActive ? "Active" : "Inactive",
      a.imageUrl || ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    // Add BOM for Excel/Google Sheets
    const csvWithBOM = BOM + csvContent;

    // Create blob with UTF-8 encoding
    const blob = new Blob([csvWithBOM], {
      type: "text/csv;charset=utf-8"
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const today = new Date().toISOString().split('T')[0];
    link.setAttribute("href", url);
    link.setAttribute("download", `announcements_${today}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);

    setToast("✅ CSV exported successfully! Open in Google Sheets/Excel");
  };

  if (loading) return <div className="loading-state"><div className="spinner"></div>Loading announcements...</div>;
  if (queryError) return <div className="error-state">Error: {queryError.message}</div>;

  return (
    <div className="announcement-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`toast ${toast.includes('❌') ? 'error' : toast.includes('✅') ? 'success' : 'info'}`}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1><FiBell /> Announcement Manager</h1>
          <p className="page-subtitle">
            Total: {announcements.length} announcements |
            Showing: {filteredAnnouncements.length} filtered |
            Page {currentPage} of {totalPages}
          </p>
        </div>
        {announcements.length > 0 && (
          <div className="header-actions">
            <div className="view-switch">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <FiGrid /> Grid
              </button>
              <button
                className={`view-btn ${viewMode === 'slides' ? 'active' : ''}`}
                onClick={() => setViewMode('slides')}
                title="Slides View"
                disabled={filteredAnnouncements.length === 0}
              >
                <FiSliders /> Slides
              </button>
            </div>
            <button
              className="export-btn"
              onClick={exportToCSV}
              title="Export to CSV (Google Sheets compatible)"
            >
              <FiDownload /> Export CSV
            </button>
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="filter-bar">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by title or message..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>

        <div className="announcement-filters">
          <div className="filter-group">
            <FiFilter />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content - Side by Side Layout */}
      <div className="side-by-side-layout">
        {/* Left Side - Form */}
        <div className="left-side">
          <div className="form-container">
            <div className="form-header">
              <h3>{editMode ? 'Edit Announcement' : 'New Announcement'}</h3>
              {editMode && (
                <button className="cancel-btn" onClick={resetForm}>
                  <FiX /> Cancel
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="announcement-form">
              <div className="form-group">
                <label><FiFileText /> Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter announcement title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  rows="3"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter announcement message"
                  required
                />
              </div>

              <div className="form-group">
                <label><FiImage /> Image</label>
                <div className="upload-area">
                  <label className="upload-label">
                    <FiUpload />
                    {isUploading ? 'Uploading...' : 'Choose Image'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      hidden
                    />
                  </label>
                  {imageUrl && (
                    <div className="upload-status">
                      <img src={imageUrl} alt="Preview" />
                      <span><FiCheck /> Uploaded</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="toggle-label">
                  <span>Status:</span>
                  <div
                    className={`status-toggle ${isActive ? 'active' : 'inactive'}`}
                    onClick={() => setIsActive(!isActive)}
                  >
                    {isActive ? <FiToggleRight /> : <FiToggleLeft />}
                    <span>{isActive ? 'Active' : 'Inactive'}</span>
                  </div>
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-outline" onClick={resetForm}>
                  Clear
                </button>
                <button type="submit" className="btn-primary">
                  {editMode ? 'Update' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Announcements */}
        <div className="right-side">
          <div className="announcements-container">
            <div className="announcements-header">
              <div className="header-left">
                <h3>All Announcements ({filteredAnnouncements.length})</h3>
                {statusFilter !== "all" && (
                  <div className="active-filter">
                    <FiFilter /> Status: {statusFilter === "active" ? "Active" : "Inactive"}
                  </div>
                )}
              </div>
              <div className="header-right">
                <div className="pagination-info">
                  Showing {Math.min(startIndex + 1, filteredAnnouncements.length)} to {Math.min(endIndex, filteredAnnouncements.length)} of {filteredAnnouncements.length} entries
                </div>
                <div className="items-per-page">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="8">8 per page</option>
                    <option value="12">12 per page</option>
                    <option value="16">16 per page</option>
                    <option value="20">20 per page</option>
                  </select>
                </div>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <>
                {filteredAnnouncements.length === 0 ? (
                  <div className="empty-content">
                    <FiBell />
                    <p>No announcements found</p>
                    {searchTerm && <p className="empty-hint">Try changing your search term</p>}
                  </div>
                ) : (
                  <>
                    <div className="cards-grid">
                      {currentItems.map((announcement) => (
                        <div
                          key={announcement.id}
                          className={`card-item ${expandedCard === announcement.id ? 'expanded' : ''}`}
                        >
                          <div className="card-top">
                            {announcement.imageUrl && (
                              <div className="card-img">
                                <img src={announcement.imageUrl} alt={announcement.title} />
                              </div>
                            )}
                            <div className="card-actions">
                              <button onClick={() => handleEdit(announcement)} className="action-btn edit" title="Edit">
                                <FiEdit2 />
                              </button>
                              <button onClick={() => handleDelete(announcement.id)} className="action-btn delete" title="Delete">
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>

                          <div className="card-body">
                            <div className="announcement-meta">
                              <span className={`status-badge ${announcement.isActive ? 'active' : 'inactive'}`}>
                                {announcement.isActive ? <FiToggleRight /> : <FiToggleLeft />}
                                {announcement.isActive ? 'Active' : 'Inactive'}
                              </span>
                              <span className="date-badge">
                                <FiCalendar /> {new Date(announcement.createdAt).toLocaleDateString()}
                              </span>
                            </div>

                            <h4 className="card-title" title={announcement.title}>
                              {announcement.title}
                            </h4>

                            {announcement.message && (
                              <div className={`card-description ${expandedCard === announcement.id ? 'expanded' : ''}`}>
                                {expandedCard === announcement.id ? announcement.message : `${announcement.message.substring(0, 60)}...`}
                                {!expandedCard && announcement.message.length > 60 && (
                                  <button
                                    className="read-more"
                                    onClick={() => toggleExpandCard(announcement.id)}
                                  >
                                    Read more
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* MOVE FOOTER OUTSIDE OF card-body like Training component */}
                          <div className="card-footer">
                            <button
                              className="status-toggle-btn"
                              onClick={() => toggleActiveStatus(announcement.id, announcement.isActive)}
                            >
                              {announcement.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              className="view-slide-btn"
                              onClick={() => {
                                setViewMode('slides');
                                const globalIndex = filteredAnnouncements.findIndex(a => a.id === announcement.id);
                                setCurrentSlide(globalIndex);
                              }}
                              title="View in slides mode"
                            >
                              <FiEye /> View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="pagination-controls">
                        <div className="pagination-buttons">
                          <button
                            className="pagination-btn first"
                            onClick={() => goToPage(1)}
                            disabled={currentPage === 1}
                          >
                            <FiChevronsLeft />
                          </button>
                          <button
                            className="pagination-btn prev"
                            onClick={prevPage}
                            disabled={currentPage === 1}
                          >
                            <FiChevronLeft />
                          </button>

                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return pageNum <= totalPages ? (
                              <button
                                key={pageNum}
                                className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                                onClick={() => goToPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            ) : null;
                          })}

                          <button
                            className="pagination-btn next"
                            onClick={nextPage}
                            disabled={currentPage === totalPages}
                          >
                            <FiChevronRight />
                          </button>
                          <button
                            className="pagination-btn last"
                            onClick={() => goToPage(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            <FiChevronsRight />
                          </button>
                        </div>
                        <div className="page-info">
                          Page {currentPage} of {totalPages}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="slides-view">
                <div className="slides-header">
                  <button className="back-btn" onClick={() => setViewMode('grid')}>
                    <FiChevronLeft /> Back to Grid
                  </button>
                  <div className="slide-nav-info">
                    <span className="slide-counter">
                      {currentSlide + 1} / {filteredAnnouncements.length}
                    </span>
                  </div>
                </div>

                <div className="slide-container">
                  <button className="nav-btn prev" onClick={prevSlide} disabled={filteredAnnouncements.length <= 1}>
                    <FiChevronLeft />
                  </button>

                  <div className="slide-content">
                    {filteredAnnouncements[currentSlide] && (
                      <div className="slide-item">
                        <div className="slide-media">
                          {filteredAnnouncements[currentSlide].imageUrl ? (
                            <img src={filteredAnnouncements[currentSlide].imageUrl} alt={filteredAnnouncements[currentSlide].title} />
                          ) : (
                            <div className="no-media">
                              <FiImage />
                              <p>No Image</p>
                            </div>
                          )}
                        </div>

                        <div className="slide-info">
                          <div className="slide-header-row">
                            <h2>{filteredAnnouncements[currentSlide].title}</h2>
                            <div
                              className={`slide-status-toggle ${filteredAnnouncements[currentSlide].isActive ? 'active' : 'inactive'}`}
                              onClick={() => toggleActiveStatus(filteredAnnouncements[currentSlide].id, filteredAnnouncements[currentSlide].isActive)}
                            >
                              {filteredAnnouncements[currentSlide].isActive ? <FiToggleRight /> : <FiToggleLeft />}
                              <span>{filteredAnnouncements[currentSlide].isActive ? 'Active' : 'Inactive'}</span>
                            </div>
                          </div>

                          <div className="slide-details">
                            <h4>Message</h4>
                            <p className="slide-full-desc">{filteredAnnouncements[currentSlide].message}</p>
                          </div>

                          <div className="slide-controls">
                            <button
                              className="btn-slide edit"
                              onClick={() => {
                                handleEdit(filteredAnnouncements[currentSlide]);
                                setViewMode('grid');
                              }}
                            >
                              <FiEdit2 /> Edit
                            </button>
                            <button
                              className="btn-slide delete"
                              onClick={() => {
                                if (window.confirm('Delete this announcement?')) {
                                  handleDelete(filteredAnnouncements[currentSlide].id);
                                  if (filteredAnnouncements.length === 1) {
                                    setViewMode('grid');
                                  } else {
                                    setCurrentSlide(Math.max(0, currentSlide - 1));
                                  }
                                }
                              }}
                            >
                              <FiTrash2 /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button className="nav-btn next" onClick={nextSlide} disabled={filteredAnnouncements.length <= 1}>
                    <FiChevronRight />
                  </button>
                </div>

                {filteredAnnouncements.length > 1 && (
                  <div className="slide-dots">
                    {filteredAnnouncements.map((_, index) => (
                      <button
                        key={index}
                        className={`dot ${index === currentSlide ? 'active' : ''}`}
                        onClick={() => goToSlide(index)}
                        title={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnnouncementPage;